import { useState, useCallback, useMemo } from 'react';
import type { Country, QuizMode, QuizQuestion, QuizAnswer, QuizSession } from '@/types';
import { useLearningStore, useStatsStore, useAchievementsStore, type QuizLength } from '@/store';
import { answerToQuality } from '@/lib/sm2';
import { shuffleArray, generateId } from '@/lib/utils';
import countries from '@/data/countries.json';

const allCountries = countries as Country[];

interface UseQuizOptions {
  mode: QuizMode;
  questionCount: number;
  regions?: string[];
}

export function useQuiz({ mode, questionCount, regions }: UseQuizOptions) {
  const { initializeCard, updateCard, cards } = useLearningStore();
  const { recordAnswer, recordSession } = useStatsStore();
  const { recordPerfectScore } = useAchievementsStore();

  const [session, setSession] = useState<QuizSession | null>(null);
  const [currentQuestionStart, setCurrentQuestionStart] = useState<number>(0);

  // Filter countries by region if specified
  const availableCountries = useMemo(() => {
    if (!regions || regions.length === 0) return allCountries;
    return allCountries.filter((c) => regions.includes(c.region));
  }, [regions]);

  // Initialize quiz session
  const startQuiz = useCallback(() => {
    // Initialize learning cards for all countries
    availableCountries.forEach((c) => initializeCard(c.code));

    // Select countries based on spaced repetition priority
    const now = Date.now();
    const sortedCountries = [...availableCountries].sort((a, b) => {
      const cardA = cards[a.code];
      const cardB = cards[b.code];

      // New cards (never seen) get high priority
      if (!cardA) return -1;
      if (!cardB) return 1;

      // Due cards come first
      const aDue = cardA.nextReviewDate <= now;
      const bDue = cardB.nextReviewDate <= now;
      if (aDue && !bDue) return -1;
      if (!aDue && bDue) return 1;

      // Then sort by next review date
      return cardA.nextReviewDate - cardB.nextReviewDate;
    });

    // Take the top N countries for this quiz
    const selectedCountries = sortedCountries.slice(0, questionCount);

    // Generate questions
    const questions: QuizQuestion[] = selectedCountries.map((country) => {
      // Get 3 random wrong options
      const otherCountries = availableCountries.filter(
        (c) => c.code !== country.code
      );
      const wrongOptions = shuffleArray(otherCountries).slice(0, 3);
      const options = shuffleArray([country, ...wrongOptions]);

      return {
        id: generateId(),
        targetCountry: country,
        options,
        mode,
      };
    });

    const newSession: QuizSession = {
      id: generateId(),
      mode,
      questions: shuffleArray(questions),
      answers: [],
      currentIndex: 0,
      startTime: Date.now(),
      completed: false,
    };

    setSession(newSession);
    setCurrentQuestionStart(Date.now());

    return newSession;
  }, [availableCountries, questionCount, mode, cards, initializeCard]);

  // Submit an answer
  const submitAnswer = useCallback(
    (selectedCode: string) => {
      if (!session || session.completed) return null;

      const question = session.questions[session.currentIndex];
      const correct = selectedCode === question.targetCountry.code;
      const timeSpent = Date.now() - currentQuestionStart;

      const answer: QuizAnswer = {
        questionId: question.id,
        countryCode: question.targetCountry.code,
        selectedCode,
        correct,
        timeSpent,
        timestamp: Date.now(),
      };

      // Update spaced repetition
      const quality = answerToQuality(correct, timeSpent);
      updateCard(question.targetCountry.code, quality);

      // Update stats
      recordAnswer(question.targetCountry.code, correct, timeSpent);

      // Update session - DON'T advance currentIndex yet, wait for nextQuestion
      const newAnswers = [...session.answers, answer];

      const updatedSession: QuizSession = {
        ...session,
        answers: newAnswers,
      };

      setSession(updatedSession);

      return answer;
    },
    [session, currentQuestionStart, updateCard, recordAnswer]
  );

  // Move to next question
  const nextQuestion = useCallback(() => {
    if (!session || session.completed) return;

    const isComplete = session.currentIndex >= session.questions.length - 1;

    if (isComplete) {
      // Mark session as complete
      const updatedSession: QuizSession = {
        ...session,
        completed: true,
        endTime: Date.now(),
      };
      setSession(updatedSession);

      const correctCount = session.answers.filter((a) => a.correct).length;
      const totalQuestions = session.questions.length;

      // Record completed session
      recordSession({
        date: Date.now(),
        mode: session.mode,
        questionsCount: totalQuestions,
        correctCount,
        duration: Date.now() - session.startTime,
        flagsCovered: session.questions.map((q) => q.targetCountry.code),
      });

      // Check for perfect score and record achievement
      if (correctCount === totalQuestions) {
        // Determine which quiz length tier this belongs to
        const quizLength: QuizLength =
          totalQuestions === allCountries.length
            ? 'all'
            : totalQuestions >= 100
              ? 100
              : totalQuestions >= 50
                ? 50
                : 20;
        recordPerfectScore(session.mode, quizLength);
      }
    } else {
      // Advance to next question
      const updatedSession: QuizSession = {
        ...session,
        currentIndex: session.currentIndex + 1,
      };
      setSession(updatedSession);
      setCurrentQuestionStart(Date.now());
    }
  }, [session, recordSession, recordPerfectScore]);

  // Get current question
  const currentQuestion = session?.questions[session.currentIndex];
  const lastAnswer = session?.answers[session.answers.length - 1];

  // Calculate stats
  const correctCount = session?.answers.filter((a) => a.correct).length ?? 0;
  const progress = session
    ? (session.currentIndex + (session.completed ? 1 : 0)) / session.questions.length
    : 0;

  return {
    session,
    currentQuestion,
    lastAnswer,
    correctCount,
    progress,
    startQuiz,
    submitAnswer,
    nextQuestion,
    isComplete: session?.completed ?? false,
  };
}
